// Словарь терминов модуля «Витрина».
// Пользователь — владелец канала, НЕ маркетолог.
// Запрещённые термины ниже не должны попадать в UI (только в развёрнутом
// уровне карточки поста, где даём точные данные для тех кто хочет деталей).
//
// Источник: showcase-spec.md §9 «Язык интерфейса (строго)».

/**
 * Запрещённые SEO-термины в UI.
 * Если видишь эти слова в JSX-литералах showcase-страниц — заменяй на UI-вариант ниже.
 */
export const FORBIDDEN_TERMS = [
  'индексация',
  'проиндексировано',
  'индексирует',
  'SERP',
  'core web vitals',
  'мета-теги',
  'мета-описание',
  'краулинг',
  'crawl',
  'noindex',
  'позиция в выдаче',
  'органический трафик',
  'SEO-оптимизация',
  'деиндексация',
  'ключевые слова',
  'поисковые ключи',
] as const

/**
 * Допустимые замены в UI: технический термин → как говорим юзеру.
 * Используется как референс при копи-райтинге UI.
 */
export const TERM_REPLACEMENTS: Record<string, string> = {
  индексация: 'виден в поиске',
  проиндексировано: 'найдено в Яндексе и Google',
  'органический трафик': 'читатели из поиска',
  'поисковые запросы': 'как вас находят',
  'ключевые слова': 'что люди ищут',
  'SEO-оптимизация': 'готовим пост к поиску',
  деиндексация: 'убрать из поиска',
  noindex: 'скрыть пост',
  'позиция в выдаче': 'место в поиске',
}

/**
 * Helper: проверить строку на запрещённые термины (для unit-тестов или dev-warnings).
 * Возвращает массив найденных нарушений.
 */
export function findForbiddenTerms(text: string): string[] {
  const lower = text.toLowerCase()
  return FORBIDDEN_TERMS.filter((term) => lower.includes(term.toLowerCase()))
}

/**
 * Статус поста в Витрине — пользовательский язык.
 * Карта от внутреннего enum к UI-тексту.
 */
export const POST_STATUS_LABEL: Record<string, string> = {
  pending: 'готовится',
  active: 'в поиске',
  hidden: 'скрыт',
  rejected: 'не прошёл проверку',
  quality_rejected: 'не прошёл проверку',
}

/**
 * Статус модуля Витрина — пользовательский язык.
 * Карта от backend status к UI-метке.
 */
export const SHOWCASE_STATUS_LABEL: Record<string, string> = {
  locked: 'недоступно на текущем тарифе',
  intro: 'не активирована',
  consent: 'согласие',
  choosing: 'выбор постов',
  processing: 'создаём страницы',
  active: 'работает',
  grace_3d: 'тариф истёк — восстановите Профи',
  disabled: 'выключена',
}

/**
 * Коды причин quality-rejected (B10) — человеческие формулировки для UI.
 * Бэк присылает технические enum'ы (`too_short`, `invalid_slug`, …).
 * Если код незнакомый — `translateRejectionReason` показывает raw,
 * чтобы юзер хотя бы мог отправить нам код для разбора.
 */
export const QUALITY_REJECTED_REASON_LABEL: Record<string, string> = {
  too_short: 'слишком короткий текст',
  empty_text: 'нет текста — только медиа',
  media_only: 'нет текста — только медиа',
  invalid_slug: 'не удалось собрать заголовок страницы',
  stop_words: 'много служебных слов или эмодзи',
  language_unsupported: 'язык поста не поддерживается',
  nsfw: 'контент не подходит для публичной страницы',
  low_quality: 'AI оценил пост как низкокачественный',
  duplicate: 'почти полное совпадение с другим постом',
  no_value: 'AI не нашёл смысловой ценности',
  title_too_long: 'не получилось сократить заголовок',
  generation_failed: 'AI не смог обработать пост — попробуем ещё раз позже',
}

export function translateRejectionReason(code: string): string {
  return QUALITY_REJECTED_REASON_LABEL[code] ?? code
}
